import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { calculateTotalPrice, getDefaultDuration } from '../utils/priceCalculator';

const CartContext = createContext();

const CART_STORAGE_KEY = 'smarttool_cart';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = sessionStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Persist cart to session storage
  useEffect(() => {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  // Get the vending machine ID from the first item (all items must be from same machine)
  const getVendingMachineId = useCallback(() => {
    return items.length > 0 ? items[0].vendingMachineId : null;
  }, [items]);

  // Check if a tool is in the cart
  const isInCart = useCallback((toolId) => {
    return items.some(item => item.tool.id === toolId);
  }, [items]);

  // Check if tool is from the same vending machine as cart items
  const isSameMachine = useCallback((vendingMachineId) => {
    const currentMachineId = getVendingMachineId();
    return currentMachineId === null || currentMachineId === vendingMachineId;
  }, [getVendingMachineId]);

  // Add item to cart
  const addItem = useCallback((tool, vendingMachineId, rentalType = '4hours', duration = 1) => {
    if (isInCart(tool.id)) return false;
    if (!isSameMachine(vendingMachineId)) return false;

    const actualDuration = duration || getDefaultDuration(rentalType);
    const price = calculateTotalPrice(tool, rentalType, actualDuration);

    setItems(prev => [...prev, {
      tool,
      vendingMachineId,
      rentalType,
      duration: actualDuration,
      price,
      addedAt: Date.now(),
    }]);

    return true;
  }, [isInCart, isSameMachine]);

  // Remove item from cart
  const removeItem = useCallback((toolId) => {
    setItems(prev => prev.filter(item => item.tool.id !== toolId));
  }, []);

  // Update item in cart (rental type or duration)
  const updateItem = useCallback((toolId, updates) => {
    setItems(prev => prev.map(item => {
      if (item.tool.id !== toolId) return item;

      const newRentalType = updates.rentalType ?? item.rentalType;
      const newDuration = updates.duration ?? item.duration;
      const newPrice = calculateTotalPrice(item.tool, newRentalType, newDuration);

      return {
        ...item,
        rentalType: newRentalType,
        duration: newDuration,
        price: newPrice,
      };
    }));
  }, []);

  // Remove cart items whose tools are no longer available (called after fresh poll)
  const removeUnavailableItems = useCallback((freshTools) => {
    setItems(prev => prev.filter(item => {
      const fresh = freshTools.find(t => t.id === item.tool.id);
      return fresh && fresh.available && !fresh.maintenance;
    }));
  }, []);

  // Recalculate prices from fresh tool data (admin may have changed them mid-session)
  const syncPrices = useCallback((freshTools) => {
    setItems(prev => prev.map(item => {
      const fresh = freshTools.find(t => t.id === item.tool.id);
      return fresh
        ? { ...item, tool: fresh, price: calculateTotalPrice(fresh, item.rentalType, item.duration) }
        : item;
    }));
  }, []);

  // Clear all items from cart
  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  // Replace entire cart (used when loading from a mobile session)
  const loadItems = useCallback((newItems) => {
    setItems(newItems);
  }, []);

  // Get total price of all items
  const getTotal = useCallback(() => {
    return items.reduce((sum, item) => sum + item.price, 0);
  }, [items]);

  // Get cart item by tool ID
  const getItem = useCallback((toolId) => {
    return items.find(item => item.tool.id === toolId);
  }, [items]);

  const value = {
    items,
    addItem,
    removeItem,
    updateItem,
    clearCart,
    loadItems,
    getTotal,
    isInCart,
    isSameMachine,
    getVendingMachineId,
    getItem,
    removeUnavailableItems,
    syncPrices,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
